"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ClinicalOpenApiOperations = void 0;
const errors_1 = require("../errors");
function errorCodesFor(endpoint) {
    return errors_1.ClinicalApiErrorRegistry[endpoint]?.errors.map((entry) => entry.code) ?? [];
}
/**
 * One entry per endpoint contract this stage defines — 25 operations
 * (22 from Stage 6C.4, plus `getClinicalDocument` and `getClinicalDocumentPdf`
 * from Discharge Summary Phase 4C, and `getDischargeSummaryForAdmission`
 * from Phase 4C's IP Billing gap closure) across 5 capability
 * groups (Encounter, Clinical Documents, Clinical Orders, Clinical
 * Timeline, Approval Workflow). `path` is illustrative
 * REST shape for when a controller layer is eventually built (this
 * stage builds no routes) — Clinical Application Services already name
 * the exact orchestration each of these would call. No "Clinical
 * Search" entry exists: no Application Service exists to back it
 * (`CLINICAL-CONTRACT-REUSE.md` §2, re-confirming Financial's own
 * Stage 5C.4 identical conclusion, not just repeating it) — see
 * `CLINICAL-API-REVIEW.md`.
 */
exports.ClinicalOpenApiOperations = [
    /*
      P0-3 — the clinical worklist read.
  
      `requestSchemaRef` names the QUERY schema, matching every other GET in this
      file (`GetPatientClinicalTimelineRequest`, `GetClinicalAuditTrailRequest`).
      An empty string is rejected by `contracts/api/*-versioning.test.ts`, which
      asserts every operation carries a non-empty ref — a GET's query parameters
      are still a request contract and are documented as one.
    */
    {
        operationId: 'listEncounters',
        method: 'GET',
        path: '/v1/clinical/encounters',
        summary: 'List encounters — the clinical worklist (filterable, paginated)',
        version: 'v1',
        tags: ['Encounter'],
        requestSchemaRef: 'ListEncountersQuery',
        responseSchemaRef: 'EncounterListItemDto',
        errorCodes: errorCodesFor('listEncounters'),
    },
    {
        operationId: 'createEncounter',
        method: 'POST',
        path: '/v1/clinical/encounters',
        summary: 'Create an Encounter (status = OPEN)',
        version: 'v1',
        tags: ['Encounter'],
        requestSchemaRef: 'CreateEncounterRequest',
        responseSchemaRef: 'EncounterWorkflowResponseDto',
        errorCodes: errorCodesFor('createEncounter'),
    },
    {
        operationId: 'openEncounter',
        method: 'POST',
        path: '/v1/clinical/encounters/{encounterId}/open',
        summary: 'Open an Encounter (OPEN -> IN_PROGRESS)',
        version: 'v1',
        tags: ['Encounter'],
        requestSchemaRef: 'OpenEncounterRequest',
        responseSchemaRef: 'EncounterWorkflowResponseDto',
        errorCodes: errorCodesFor('openEncounter'),
    },
    {
        operationId: 'suspendEncounter',
        method: 'POST',
        path: '/v1/clinical/encounters/{encounterId}/suspend',
        summary: 'Suspend an in-progress Encounter (audit + event only, no persisted status change)',
        version: 'v1',
        tags: ['Encounter'],
        requestSchemaRef: 'SuspendEncounterRequest',
        responseSchemaRef: 'EncounterWorkflowResponseDto',
        errorCodes: errorCodesFor('suspendEncounter'),
    },
    {
        operationId: 'resumeEncounter',
        method: 'POST',
        path: '/v1/clinical/encounters/{encounterId}/resume',
        summary: 'Resume a suspended Encounter (audit + event only, no persisted status change)',
        version: 'v1',
        tags: ['Encounter'],
        requestSchemaRef: 'ResumeEncounterRequest',
        responseSchemaRef: 'EncounterWorkflowResponseDto',
        errorCodes: errorCodesFor('resumeEncounter'),
    },
    {
        operationId: 'completeEncounter',
        method: 'POST',
        path: '/v1/clinical/encounters/{encounterId}/complete',
        summary: 'Complete an Encounter (IN_PROGRESS -> PENDING_SIGNATURE, requires >=1 Clinical Document)',
        version: 'v1',
        tags: ['Encounter'],
        requestSchemaRef: 'CompleteEncounterRequest',
        responseSchemaRef: 'EncounterWorkflowResponseDto',
        errorCodes: errorCodesFor('completeEncounter'),
    },
    {
        operationId: 'closeEncounter',
        method: 'POST',
        path: '/v1/clinical/encounters/{encounterId}/close',
        summary: 'Close an Encounter (PENDING_SIGNATURE -> SIGNED)',
        version: 'v1',
        tags: ['Encounter'],
        requestSchemaRef: 'CloseEncounterRequest',
        responseSchemaRef: 'EncounterWorkflowResponseDto',
        errorCodes: errorCodesFor('closeEncounter'),
    },
    {
        operationId: 'cancelEncounter',
        method: 'POST',
        path: '/v1/clinical/encounters/{encounterId}/cancel',
        summary: 'Cancel an Encounter (soft delete; refused once SIGNED/AMENDED)',
        version: 'v1',
        tags: ['Encounter'],
        requestSchemaRef: 'CancelEncounterRequest',
        responseSchemaRef: 'CancelEncounterResponseDto',
        errorCodes: errorCodesFor('cancelEncounter'),
    },
    {
        operationId: 'createClinicalDocument',
        method: 'POST',
        path: '/v1/clinical/documents',
        summary: 'Create a Clinical Document (status = DRAFT)',
        version: 'v1',
        tags: ['Clinical Documents'],
        requestSchemaRef: 'CreateClinicalDocumentRequest',
        responseSchemaRef: 'ClinicalDocumentWorkflowResponseDto',
        errorCodes: errorCodesFor('createClinicalDocument'),
    },
    {
        operationId: 'saveClinicalDocumentDraft',
        method: 'POST',
        path: '/v1/clinical/documents/{documentId}/draft',
        summary: 'Save a Clinical Document draft (appends a new version row)',
        version: 'v1',
        tags: ['Clinical Documents'],
        requestSchemaRef: 'SaveClinicalDocumentDraftRequest',
        responseSchemaRef: 'ClinicalDocumentWorkflowResponseDto',
        errorCodes: errorCodesFor('saveClinicalDocumentDraft'),
    },
    {
        operationId: 'finalizeClinicalDocument',
        method: 'POST',
        path: '/v1/clinical/documents/{documentId}/finalize',
        summary: 'Finalize (sign) a Clinical Document — DRAFT -> FINAL or PENDING_COSIGNATURE',
        version: 'v1',
        tags: ['Clinical Documents'],
        requestSchemaRef: 'FinalizeClinicalDocumentRequest',
        responseSchemaRef: 'ClinicalDocumentWorkflowResponseDto',
        errorCodes: errorCodesFor('finalizeClinicalDocument'),
    },
    {
        operationId: 'approveClinicalDocument',
        method: 'POST',
        path: '/v1/clinical/documents/{documentId}/approve',
        summary: 'Approve (co-sign) a Clinical Document, gated by a real role-validation check — PENDING_COSIGNATURE -> FINAL',
        version: 'v1',
        tags: ['Clinical Documents', 'Approval Workflow'],
        requestSchemaRef: 'ApproveClinicalDocumentRequest',
        responseSchemaRef: 'ClinicalDocumentWorkflowResponseDto',
        errorCodes: errorCodesFor('approveClinicalDocument'),
    },
    {
        operationId: 'amendClinicalDocument',
        method: 'POST',
        path: '/v1/clinical/documents/{originalDocumentId}/amend',
        summary: 'Amend a FINAL Clinical Document (creates a new, independent DRAFT document row)',
        version: 'v1',
        tags: ['Clinical Documents'],
        requestSchemaRef: 'AmendClinicalDocumentRequest',
        responseSchemaRef: 'ClinicalDocumentWorkflowResponseDto',
        errorCodes: errorCodesFor('amendClinicalDocument'),
    },
    {
        operationId: 'getClinicalDocumentVersionHistory',
        method: 'GET',
        path: '/v1/clinical/documents/{documentId}/versions',
        summary: 'Get a Clinical Document\'s full version history (up to 100 rows, no pagination)',
        version: 'v1',
        tags: ['Clinical Documents', 'Version History'],
        requestSchemaRef: 'GetClinicalDocumentVersionHistoryRequest',
        responseSchemaRef: 'ClinicalDocumentVersionHistoryResponseDto',
        errorCodes: errorCodesFor('getClinicalDocumentVersionHistory'),
    },
    /*
      Discharge Summary, Phase 4B — Step 1 inspection found no single-document
      retrieval endpoint anywhere in Clinical's 6 real document routes. Added
      as a plain, tenant-scoped read, same discipline as
      `getClinicalDocumentVersionHistory` immediately above.
    */
    {
        operationId: 'getClinicalDocument',
        method: 'GET',
        path: '/v1/clinical/documents/{documentId}',
        summary: 'Get a single Clinical Document by id',
        version: 'v1',
        tags: ['Clinical Documents'],
        requestSchemaRef: 'GetClinicalDocumentRequest',
        responseSchemaRef: 'GetClinicalDocumentResponseDto',
        errorCodes: errorCodesFor('getClinicalDocument'),
    },
    /*
      Discharge Summary, Phase 4C — View + Print. Returns a FILE, not the JSON
      envelope (`responseSchemaRef: 'application/pdf'`, the same convention
      `ip-billing.operations.ts`'s `printIpDocument` already established).
      Takes the identical `{documentId}` param shape as `getClinicalDocument`
      immediately above, so it reuses that same request schema rather than
      declaring a new one.
    */
    {
        operationId: 'getClinicalDocumentPdf',
        method: 'GET',
        path: '/v1/clinical/documents/{documentId}/pdf',
        summary: 'Render a Clinical Document to PDF (read-only; never mutates document state)',
        version: 'v1',
        tags: ['Clinical Documents'],
        requestSchemaRef: 'GetClinicalDocumentRequest',
        responseSchemaRef: 'application/pdf',
        errorCodes: errorCodesFor('getClinicalDocumentPdf'),
    },
    /*
      Discharge Summary, Phase 4C — IP Billing's own "Discharge Summary" action
      needs to know, for its selected admission, whether one already exists.
      Always 200 with `data: null` when none does — that is the normal,
      expected state, not an error — so no 404 is ever part of this contract.
    */
    {
        operationId: 'getDischargeSummaryForAdmission',
        method: 'GET',
        path: '/v1/clinical/documents/by-admission/{ipAdmissionId}/discharge-summary',
        summary: "Look up whether an IP admission already has a Discharge Summary — null if none exists yet",
        version: 'v1',
        tags: ['Clinical Documents'],
        requestSchemaRef: 'GetDischargeSummaryForAdmissionRequest',
        responseSchemaRef: 'DischargeSummaryForAdmissionResponseDto',
        errorCodes: errorCodesFor('getDischargeSummaryForAdmission'),
    },
    {
        operationId: 'createClinicalOrder',
        method: 'POST',
        path: '/v1/clinical/orders',
        summary: 'Create a Clinical Order (status = DRAFT) — Medication/Laboratory/Radiology/Procedure/Nursing/Referral',
        version: 'v1',
        tags: ['Clinical Orders'],
        requestSchemaRef: 'CreateClinicalOrderRequest',
        responseSchemaRef: 'ClinicalOrderWorkflowResponseDto',
        errorCodes: errorCodesFor('createClinicalOrder'),
    },
    {
        operationId: 'signClinicalOrder',
        method: 'POST',
        path: '/v1/clinical/orders/{orderId}/sign',
        summary: 'Sign a Clinical Order (DRAFT -> SIGNED -> TRANSMITTED in one call, no approval gate)',
        version: 'v1',
        tags: ['Clinical Orders'],
        requestSchemaRef: 'SignClinicalOrderRequest',
        responseSchemaRef: 'ClinicalOrderWorkflowResponseDto',
        errorCodes: errorCodesFor('signClinicalOrder'),
    },
    {
        operationId: 'signClinicalOrderWithApproval',
        method: 'POST',
        path: '/v1/clinical/orders/{orderId}/sign-with-approval',
        summary: 'Sign a Clinical Order, gated by a real role-validation check against any configured ApprovalMatrix entry for orderType',
        version: 'v1',
        tags: ['Clinical Orders', 'Approval Workflow'],
        requestSchemaRef: 'SignClinicalOrderWithApprovalRequest',
        responseSchemaRef: 'ClinicalOrderWorkflowResponseDto',
        errorCodes: errorCodesFor('signClinicalOrderWithApproval'),
    },
    {
        operationId: 'cancelClinicalOrder',
        method: 'POST',
        path: '/v1/clinical/orders/{orderId}/cancel',
        summary: 'Cancel a Clinical Order',
        version: 'v1',
        tags: ['Clinical Orders'],
        requestSchemaRef: 'CancelClinicalOrderRequest',
        responseSchemaRef: 'ClinicalOrderWorkflowResponseDto',
        errorCodes: errorCodesFor('cancelClinicalOrder'),
    },
    {
        operationId: 'getPatientClinicalTimeline',
        method: 'GET',
        path: '/v1/clinical/patients/{patientId}/timeline',
        summary: 'Get a patient\'s longitudinal Clinical Timeline (Encounter/Document/Order/Vitals/Diagnosis/Problem, merged, sorted descending)',
        version: 'v1',
        tags: ['Clinical Timeline'],
        requestSchemaRef: 'GetPatientClinicalTimelineRequest',
        responseSchemaRef: 'PatientClinicalTimelineResponseDto',
        errorCodes: errorCodesFor('getPatientClinicalTimeline'),
    },
    {
        operationId: 'getPatientClinicalWorkflowTimeline',
        method: 'GET',
        path: '/v1/clinical/patients/{patientId}/workflow-timeline',
        summary: 'Get a patient\'s Clinical Timeline with each entry\'s own scoped audit trail attached',
        version: 'v1',
        tags: ['Clinical Timeline'],
        requestSchemaRef: 'GetPatientClinicalWorkflowTimelineRequest',
        responseSchemaRef: 'PatientClinicalWorkflowTimelineResponseDto',
        errorCodes: errorCodesFor('getPatientClinicalWorkflowTimeline'),
    },
    {
        operationId: 'getClinicalAuditTrail',
        method: 'GET',
        path: '/v1/clinical/audit/{entityType}/{entityId}',
        summary: 'Get the full audit trail for one specific Clinical entity',
        version: 'v1',
        tags: ['Clinical Timeline'],
        requestSchemaRef: 'GetClinicalAuditTrailRequest',
        responseSchemaRef: 'ClinicalAuditTrailResponseDto',
        errorCodes: errorCodesFor('getClinicalAuditTrail'),
    },
    {
        operationId: 'resolveClinicalApprovalChain',
        method: 'GET',
        path: '/v1/clinical/approval-chains/{moduleName}/{actionName}',
        summary: 'Resolve the approval chain (who must co-sign, in what order) for a module/action — chain resolution only, no approval-state tracking',
        version: 'v1',
        tags: ['Approval Workflow'],
        requestSchemaRef: 'ResolveClinicalApprovalChainRequest',
        responseSchemaRef: 'ResolveClinicalApprovalChainResponseDto',
        errorCodes: errorCodesFor('resolveClinicalApprovalChain'),
    },
    {
        operationId: 'validateClinicalCoSigner',
        method: 'POST',
        path: '/v1/clinical/approval-chains/{moduleName}/{actionName}/validate-co-signer',
        summary: 'Validate whether a set of role IDs satisfies the resolved approval chain — never throws, returns a decision',
        version: 'v1',
        tags: ['Approval Workflow'],
        requestSchemaRef: 'ValidateClinicalCoSignerRequest',
        responseSchemaRef: 'ValidateClinicalCoSignerResponseDto',
        errorCodes: errorCodesFor('validateClinicalCoSigner'),
    },
];
//# sourceMappingURL=clinical.operations.js.map